import { Stack } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import { GenericAppError } from '@side/pantry';
import { useRouter } from 'next/router';
import { Spinner } from '@/components/Spinner';
import { useViewReportAs } from '../helpers';
import { ReportContainer } from '../ReportContainer/ReportContainer';
import { useReportsRole } from '../ReportsConfiguration/useReportsRole';
import { PlatformFeeSummaryCards } from './components/PlatformFeeSummaryCards';
import { PlatformFeeTable } from './components/PlatformFeeTable';
import { useGetPlatformFeeData } from './hooks/useGetPlatformFeeData';

export function PlatformFeeReport() {
  const router = useRouter();
  const isEmbedded = router.query.embedded === 'true';
  const viewingAs = useViewReportAs();
  const { data: session, status: sessionStatus } = useSession();
  const { isPartner, isLoading: isRoleLoading } = useReportsRole();
  const isAccessLoading = sessionStatus === 'loading' || isRoleLoading;
  const canAccess =
    (session?.hasScopes?.(['reports:read']) || isPartner) ?? false;

  const {
    rows,
    chargedCount,
    exemptCount,
    missingPaymentCount,
    isPartnerPay,
    isLoading: isDataLoading,
    error: dataError,
  } = useGetPlatformFeeData({
    viewReportAs: viewingAs ?? '',
    skip: isAccessLoading || !canAccess,
  });

  if (!viewingAs) return null;

  if (isAccessLoading) {
    return <Spinner fullScreen />;
  }

  if (!canAccess) {
    return <GenericAppError />;
  }

  if (dataError) {
    return <GenericAppError title="Something went wrong loading the report" />;
  }

  return (
    <ReportContainer
      viewReportAs={viewingAs}
      title="Platform Fee Report"
      subtitle="Review the status of platform fees, payment methods, and exemptions for all team members"
      isEmbedded={isEmbedded}
    >
      <Stack sx={{ gap: { xs: 6, md: 4.5 } }}>
        <PlatformFeeSummaryCards
          chargedCount={chargedCount}
          exemptCount={exemptCount}
          missingPaymentCount={missingPaymentCount}
          isPartnerPay={isPartnerPay}
          isLoading={isDataLoading}
        />
        <PlatformFeeTable rows={rows} isLoading={isDataLoading} />
      </Stack>
    </ReportContainer>
  );
}
